import { useMemo } from "react";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/dist/query";

import { selectCurrentAllocation, useGetAccountUsersQuery } from "fond/api";
import { consumesLicenseSeat } from "fond/utils/allocation";

import { useGetBillingCycles } from "./useGetBillingCycles";

/**
 * Determines if the current account has over-allocated licenses.
 * Returns true if more licenses have been assigned to users than are included in the subscription.
 *
 * @returns boolean - true if licenses are over-allocated, false otherwise
 */
export const useIsLicenseOverallocated = (): boolean => {
  const currentAllocation = useSelector(selectCurrentAllocation);
  const accountId = currentAllocation?.Account.ID;
  const { data: allocations } = useGetAccountUsersQuery(accountId ?? skipToken);
  const { active: currentBillingCycle } = useGetBillingCycles();

  return useMemo(() => {
    const licenseLimit = currentBillingCycle?.LicenseCount ?? 0;
    const totalAssigned = allocations?.filter(consumesLicenseSeat).length ?? 0;
    return licenseLimit > 0 && totalAssigned > licenseLimit;
  }, [allocations, currentBillingCycle]);
};
